import { Center, ColorProps, Flex } from '@chakra-ui/react'
import React from 'react'

import { IconCheckmark } from '~shared-components/icons/brand/IconCheckmark'
import { IconCircleAlert } from '~shared-components/icons/brand/IconCircleAlert'
import { IconCircleInfo } from '~shared-components/icons/brand/IconCircleInfo'
import { IconClose } from '~shared-components/icons/brand/IconClose'

export interface InfoMessageProps {
  fullWidth?: boolean
  highlight?: ColorProps['color']
  icon?: JSX.Element
  narrow?: boolean
  onClick?: () => void
  secondaryHighlight?: ColorProps['color']
  solid?: boolean
  type?: Style
}

type Style = 'error' | 'warning' | 'success' | 'info'

export const InfoMessage: React.FC<InfoMessageProps> = ({
  children,
  fullWidth,
  highlight,
  icon,
  narrow = false,
  onClick,
  secondaryHighlight,
  solid = false,
  type,
}) => {
  const chooseIcon = (
    type: InfoMessageProps['type'],
  ): {
    color: ColorProps['color']
    icon: JSX.Element | undefined
  } => {
    switch (type) {
      case 'error':
        return {
          color: 'red.70',
          icon: <IconCircleAlert boxSize="24px" />,
        }

      case 'success':
        return { color: 'green.50', icon: <IconCheckmark boxSize="24px" /> }

      case 'warning':
        return { color: 'yellow.50', icon: <IconCircleAlert boxSize="24px" /> }

      case 'info':
      default:
        return { color: 'blue.50', icon: <IconCircleInfo boxSize="24px" /> }
    }
  }

  const { color: colorByType, icon: iconByType } = chooseIcon(type)
  const color = highlight || colorByType
  const secondaryColor = secondaryHighlight || 'white'

  return (
    <Flex
      color={secondaryHighlight || color}
      fontSize={narrow ? 'tiny' : 'body'}
      whiteSpace="pre-line"
      width={fullWidth ? '100%' : undefined}
    >
      {(icon || iconByType) && (
        <Flex
          alignItems="center"
          backgroundColor={color}
          border={`1px solid ${color}`}
          flexShrink={0}
          justifyContent="center"
          padding={narrow ? '8px' : '14px'}
          paddingRight={solid ? 0 : undefined}
          sx={{ svg: { color: secondaryColor, fill: secondaryColor } }}
        >
          <Center>{icon || iconByType}</Center>
        </Flex>
      )}
      <Flex
        alignItems="center"
        backgroundColor={solid ? color : 'white'}
        border="1px"
        borderColor={color}
        borderLeft={0}
        flex={fullWidth ? 1 : undefined}
        height="100%"
        justifyContent="space-between"
        padding={narrow ? '8px' : '15px'}
      >
        {children}
        {onClick !== undefined && (
          <IconClose
            boxSize={narrow ? '12px' : '16px'}
            color={color}
            cursor="pointer"
            ml="15px"
            onClick={onClick}
          />
        )}
      </Flex>
    </Flex>
  )
}
